import { RocketOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Skeleton, Tag, Tooltip } from "antd";
import { motion, Variants } from "framer-motion";
import { User } from "lucide-react";
import { useCallback, useState } from "react";
import { CiDeliveryTruck } from "react-icons/ci";
import { GoHeart, GoHeartFill } from "react-icons/go";
import { IoDiamondOutline } from "react-icons/io5";
import { TbRotateClockwise2, TbTrashX } from "react-icons/tb";
import { MdSell } from "react-icons/md";
import { TiEdit } from "react-icons/ti";
import { VscVerifiedFilled } from "react-icons/vsc";
import { useInView } from 'react-intersection-observer';
import { Link } from "react-router-dom";
import { TimeDisplay } from "../../components/ui/TimeDisplay";
import { useAuth } from "../../Hooks/useAuth";
import { BOOST_DURATION_DAYS, BOOST_PRICE_XOF } from "../../Hooks/useAdBoosts";
import { Ad } from "../../types/adType";
import '../home/components/homStyle.css';

interface AdsCardProps {
    ad: Ad;
    images?: string[];
    isFavorite?: boolean;
    isTogglingFavorite?: boolean;
    onToggleFavorite?: () => void;
    onEdit?: () => void;
    onDeleteAd?: () => void;
    onToggleSold?: () => void;
    isTogglingSold?: boolean;
    onBoost?: () => void;
    isBoosting?: boolean;
    showEdit?: boolean;
    list?: boolean;
    onDeleteLoading?: boolean;
    isPremiumUser?: boolean;
    isPremiumUserLoading?: boolean;
    onClick?: () => void;
    // When true, sold/deleted ads render without an image or navigation:
    // the image area becomes a plain status placeholder and the card
    // can't be clicked through to the ad's page. Used on pages (like
    // favorites) that intentionally keep showing inactive ads.
    disableInactiveInteraction?: boolean;
}

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const AdsCard: React.FC<AdsCardProps> = ({
    ad,
    images = [],
    isFavorite = false,
    isTogglingFavorite = false,
    onToggleFavorite,
    onEdit,
    onDeleteAd,
    onToggleSold,
    isTogglingSold = false,
    onBoost,
    isBoosting = false,
    showEdit = false,
    list = false,
    isPremiumUser = false,
    onDeleteLoading = false,
    isPremiumUserLoading = false,
    onClick,
    disableInactiveInteraction = false
}) => {
    const { user } = useAuth();
    const createSlug = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

    const adLink = ad.categories?.name && ad.subcategories?.name
        ? `/${createSlug(ad.categories?.name)}/${createSlug(ad.subcategories?.name)}/${ad.id}`
        : "#";

    const [imageLoaded, setImageLoaded] = useState(false);
    const [ref, inView] = useInView({
        triggerOnce: true,
        rootMargin: '50px 0px',
    });

    const handleImageLoad = useCallback(() => {
        setImageLoaded(true);
    }, []);

    const handleImageError = useCallback(() => {
        setImageLoaded(true);
    }, []);

    const displayPrice = ad.price ? `${ad.price}` : "Gratuit";
    const displayImages = images.length > 0
        ? images
        : ad.product_images?.map((img: { url: string }) => img.url) || [];

    const displayLocation =
        ad.cities?.name ||
        ad.city_name ||
        ad.states?.name ||
        ad.state_name ||
        "Aucun lieu";

    const posterInfo = ad.profiles || null;

    const isInactive = !!(ad.is_sold || ad.is_deleted);
    const disableInteraction = disableInactiveInteraction && isInactive;

    const handleAdClick = () => {
        // Save current scroll position to sessionStorage
        sessionStorage.setItem('adsScrollPosition', window.scrollY.toString());
    };

    return (
        <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={cardVariants}
            transition={{ duration: 0.3 }}
            className={`ad-card group ${list ? 'flex flex-col sm:flex-row gap-4' : 'flex flex-col'} bg-white rounded-lg shadow-md overflow-hidden h-full ${ad.is_boosted ? 'border-2 border-orange-400 premium-badge' : ad.is_premium ? 'border-2 border-amber-400 premium-badge' : 'hover:shadow-lg transition-shadow'}`}
        >
            {/* Image Container */}
            {disableInteraction ? (
                <div
                    className={`block relative cursor-not-allowed ${list ? 'sm:w-1/3 md:w-1/4' : 'w-full'}`}
                    aria-disabled="true"
                >
                    <div className={`relative flex items-center justify-center bg-gray-200 ${list
                        ? 'h-48 sm:h-full w-full'
                        : 'aspect-[4/3] w-full'}`}
                    >
                        <span className={`text-sm font-bold uppercase tracking-wide ${ad.is_deleted ? 'text-gray-600' : 'text-primary-default'}`}>
                            {ad.is_deleted ? 'Annonce supprimée' : 'Annonce vendue'}
                        </span>
                    </div>

                    {/* Favorite Button stays actionable so users can still clean up their list */}
                    {onToggleFavorite && (
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onToggleFavorite();
                            }}
                            className={`absolute bottom-2 right-2 p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-sm hover:bg-white transition-colors ${isTogglingFavorite ? 'opacity-50' : ''}`}
                            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                        >
                            {isFavorite ? (
                                <GoHeartFill size={18} className="text-red-500" />
                            ) : (
                                <GoHeart size={18} className="text-primary-default" />
                            )}
                        </button>
                    )}
                </div>
            ) : (
                <Link
                    to={adLink}
                    onClick={() => {
                        if (onClick) {
                            onClick();
                        }
                        handleAdClick();
                    }}
                    className={`block relative ${list ? 'sm:w-1/3 md:w-1/4' : 'w-full'}`}
                >
                    <div className={`relative ${list
                        ? 'h-48 w-full'
                        : 'aspect-[4/3] w-full'}`}
                    >
                        {inView && (
                            <img
                                src={displayImages[0] || ad.product_images?.[0]?.url || '/placeholder.jpg'}
                                alt={ad.title}
                                className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                                loading="lazy"
                                onLoad={handleImageLoad}
                                onError={handleImageError}
                            />
                        )}
                        {!imageLoaded && (
                            <Skeleton.Image active className="absolute inset-0" />
                        )}

                        {/* Status Badges - Positioned on image */}
                        <div className="absolute top-0 left-0 right-0 flex justify-between">
                            <div>
                                {ad.is_deleted ? (
                                    <span className="bg-gray-700 text-light-default text-xs py-1 px-2 rounded-br">
                                        Supprimé
                                    </span>
                                ) : ad.is_sold && (
                                    <span className="bg-primary-default text-light-default text-xs py-1 px-2 rounded-br">
                                        Vendu
                                    </span>
                                )}
                            </div>
                            <div>
                                {ad.is_boosted ? (
                                    <div className="flex items-center gap-1 bg-orange-100 text-orange-700 text-xs py-1 px-2 rounded-bl-lg shadow-sm">
                                        <span className="font-medium">Boosté</span>
                                        <RocketOutlined className="text-orange-600" />
                                    </div>
                                ) : ad.is_premium && (
                                    <div className="flex items-center gap-1 bg-amber-100 text-amber-700 text-xs py-1 px-2 rounded-bl-lg shadow-sm">
                                        <span className="font-medium">Supreme</span>
                                        <IoDiamondOutline className="text-amber-600" />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Favorite Button - Positioned on image */}
                        {onToggleFavorite && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onToggleFavorite();
                                }}
                                className={`absolute bottom-2 right-2 p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-sm hover:bg-white transition-colors ${isTogglingFavorite ? 'opacity-50' : ''}`}
                                aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                            >
                                {isFavorite ? (
                                    <GoHeartFill size={18} className="text-red-500" />
                                ) : (
                                    <GoHeart size={18} className="text-primary-default" />
                                )}
                            </button>
                        )}
                    </div>
                </Link>
            )}

            {/* Content Container */}
            <div className={`flex-1 p-3 flex flex-col ${list ? 'justify-between' : ''}`}>
                {/* Main Content */}
                <div>
                    {/* Price and Tags Row */}
                    <div className="flex flex-wrap items-center justify-between gap-1 mb-1">
                        <span className="font-bold text-primary-default">
                            CFA {displayPrice}
                        </span>

                        <div className="flex items-center gap-1">
                            {ad.is_deliverable && (
                                <Tooltip title="Livraison disponible">
                                    <span className="text-xs border border-orange-500 flex items-center bg-orange-50 px-1 rounded-lg text-orange-400">
                                        <CiDeliveryTruck size={14} />
                                    </span>
                                </Tooltip>
                            )}
                            {ad.subcategories?.name && (
                                <Tag color="cyan" className="category-chip m-0 text-xs py-0" style={{ fontSize: '10px' }}>
                                    {ad.subcategories.name}
                                </Tag>
                            )}
                        </div>
                    </div>

                    {/* Title */}
                    {disableInteraction ? (
                        <h3 className="font-medium text-sm text-content-secondary line-clamp-1 mb-0.5">{ad.title}</h3>
                    ) : (
                        <Link to={adLink} className="block">
                            <h3 className="font-medium text-sm text-content-secondary line-clamp-1 mb-0.5">{ad.title}</h3>
                        </Link>
                    )}

                    {/* Description - Only visible in list mode or on hover in grid mode */}
                    {list ? (
                        <p className="line-clamp-2 text-xs text-content-secondary mt-1">
                            {ad.description}
                        </p>
                    ) : (
                        <p className="line-clamp-1 group-hover:line-clamp-2 text-xs text-content-secondary transition-all duration-200 mt-0.5">
                            {ad.description}
                        </p>
                    )}

                    {/* Location and Time */}
                    <div className="flex items-center justify-between mt-auto pt-2 text-xs text-content-secondary">
                        <Tag color="orange" className="text-xs py-0">{displayLocation}</Tag>
                    </div>
                    <TimeDisplay date={ad.published_at} prefix="" className="text-xs opacity-75 mt-0.5" />
                </div>

                {/* User Section - Only show in list view or on hover in grid view.
                    Hidden when the ad's owner has been deleted/deactivated
                    (ad.is_deleted), so a removed account's name doesn't keep
                    surfacing on other users' favorites lists. Sold ads (owner
                    still active) keep showing the seller name as before. */}
                {posterInfo && !ad.is_deleted && (list || true) && (
                    <div className={`flex justify-between items-center border-t pt-2 mt-2 ${!list && 'opacity-70 group-hover:opacity-100 transition-opacity'}`}>
                        <div className="flex items-center gap-1.5">
                            <User size={14} className="text-primary-default" />
                            <p className="text-xs text-content-secondary font-medium m-0 truncate max-w-[100px]">
                                {posterInfo.name}
                            </p>
                        </div>
                        {posterInfo.is_verified && (
                            <div className="flex items-center gap-1 bg-primary-accent-default text-xs py-1x-1 rounded-lg text-accent-default">
                                <span className="text-xs">Verifié</span>
                                <VscVerifiedFilled size={12} />
                            </div>
                        )}
                    </div>
                )}

                {/* Edit/Delete Actions */}
                {ad.user_id === user?.id && showEdit && (
                    ad.is_deleted ? (
                        <div className="border-t pt-2 mt-2 text-xs text-content-tertiary italic">
                            Cette annonce a été supprimée
                        </div>
                    ) : (
                        <div className="border-t pt-2 mt-2 flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                {isPremiumUser && !ad.is_sold && (
                                    <Button
                                        onClick={onEdit}
                                        loading={isPremiumUserLoading}
                                        size="small"
                                        className="text-primary-default flex items-center flex-1 text-xs"
                                    >
                                        <TiEdit className="mr-1" size={14} />
                                        Modifier
                                    </Button>
                                )}
                                {onBoost && !ad.is_sold && !ad.is_premium && (
                                    <Popconfirm
                                        title={`Booster cette annonce pour ${BOOST_PRICE_XOF} CFA (${BOOST_DURATION_DAYS} jours) ?`}
                                        description="Votre annonce sera mise en avant une fois le paiement vérifié."
                                        onConfirm={onBoost}
                                        okButtonProps={{ loading: isBoosting }}
                                    >
                                        <Button
                                            size="small"
                                            loading={isBoosting}
                                            className="text-amber-600 flex items-center flex-1 text-xs"
                                        >
                                            <RocketOutlined className="mr-1" />
                                            Booster
                                        </Button>
                                    </Popconfirm>
                                )}
                                <Popconfirm
                                    title="Supprimer cette annonce ?"
                                    onConfirm={onDeleteAd}
                                    okButtonProps={{ loading: onDeleteLoading }}
                                >
                                    <Button
                                        size="small"
                                        className="text-red-500 flex items-center flex-1 text-xs"
                                    >
                                        <TbTrashX className="mr-1" size={14} />
                                        Supprimer
                                    </Button>
                                </Popconfirm>
                            </div>
                            {onToggleSold && (
                                <Popconfirm
                                    title={ad.is_sold ? "Remettre cette annonce en vente ?" : "Marquer cette annonce comme vendue ?"}
                                    onConfirm={onToggleSold}
                                    okButtonProps={{ loading: isTogglingSold }}
                                >
                                    <Button
                                        size="small"
                                        block
                                        className={`flex items-center justify-center text-xs ${ad.is_sold ? 'text-emerald-600' : 'text-blue-600'}`}
                                    >
                                        {ad.is_sold ? (
                                            <>
                                                <TbRotateClockwise2 className="mr-1" size={14} />
                                                Remettre en vente
                                            </>
                                        ) : (
                                            <>
                                                <MdSell className="mr-1" size={14} />
                                                Marquer vendu
                                            </>
                                        )}
                                    </Button>
                                </Popconfirm>
                            )}
                        </div>
                    )
                )}
            </div>
        </motion.div>
    );
};

export default AdsCard;